// frontend/src/pages/ProfilePage.tsx
// PRC-030: Mi perfil con las piezas del recorrido: datos personales y seguridad
// en dos bloques; el cambio de contraseña pide la actual, la nueva y su repetición.

import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { Box } from '@mui/material';

import axiosInstance from '../config/axiosInstance';
import PageHeader, { HeaderTag } from '../modules/pricingJourney/components/PageHeader';
import PasswordField from '../modules/pricingJourney/components/PasswordField';
import { JourneyField, Notice, StatusBlock } from '../modules/pricingJourney/components/JourneyBlocks';
import { JourneyButton, JourneyCard, JourneyText } from '../modules/pricingJourney/components/JourneyBasics';
import { focusFirstError, MIN_PASSWORD_LENGTH, OFFLINE_MESSAGE } from '../modules/pricingJourney/journeyAuth';

// --- TYPES ---
interface UserProfile {
    id: string;
    email: string;
    full_name: string;
    company_name: string;
    tier_id: string;
    stripe_customer_id: string;
}

interface PricingAppContext {
    pricing_role: string;
    account?: {
        account_name?: string | null;
    };
    profile?: {
        company_name?: string | null;
        tier_id?: string | null;
    };
    tier?: {
        id?: string | null;
        name?: string | null;
    };
}

interface ProfileUpdatePayload {
    full_name?: string;
    company_name?: string;
}

interface PasswordUpdatePayload {
    current_password: string;
    new_password: string;
}

type PasswordErrors = Partial<Record<'current' | 'next' | 'repeat', string>>;

// --- API SERVICES ---
const fetchUserProfile = async (): Promise<UserProfile> => {
    // Identidad personal del actor autenticado.
    const { data } = await axiosInstance.get('/auth/me');
    return data;
};

const fetchPricingAppContext = async (): Promise<PricingAppContext> => {
    // Contexto operativo compartido de la cuenta Pricing.
    const { data } = await axiosInstance.get('/auth/pricing-app-context');
    return data;
};

const updateUserProfile = async (profileData: ProfileUpdatePayload): Promise<unknown> => {
    const { data } = await axiosInstance.put('/auth/profile', profileData);
    return data;
};

const updateUserPassword = async (passwordData: PasswordUpdatePayload): Promise<unknown> => {
    const { data } = await axiosInstance.put('/auth/password', passwordData);
    return data;
};

const errorCode = (err: unknown): string | undefined => {
    if (!axios.isAxiosError(err)) return undefined;
    const detail = err.response?.data?.detail;
    return typeof detail === 'object' && detail ? detail.code : undefined;
};

// --- COMPONENTE ---
const ProfilePage: React.FC = () => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    const [fullName, setFullName] = useState('');
    const [nameError, setNameError] = useState<string | undefined>();
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({});
    const [passwordFormError, setPasswordFormError] = useState<string | null>(null);

    const { data: profile, isLoading, error, refetch } = useQuery<UserProfile, Error>({
        queryKey: ['userProfile'],
        queryFn: fetchUserProfile,
        retry: 1,
    });

    const {
        data: pricingContext,
        isLoading: isPricingContextLoading,
        error: pricingContextError,
        refetch: refetchPricingContext,
    } = useQuery<PricingAppContext, Error>({
        queryKey: ['pricingAppContext'],
        queryFn: fetchPricingAppContext,
        retry: 1,
    });

    const planLabel =
        pricingContext?.tier?.name ||
        pricingContext?.tier?.id ||
        pricingContext?.profile?.tier_id ||
        null;

    const companyName =
        pricingContext?.profile?.company_name ||
        pricingContext?.account?.account_name ||
        '';

    useEffect(() => {
        if (profile) {
            setFullName(profile.full_name || '');
        }
    }, [profile]);

    const profileMutation = useMutation({
        mutationFn: updateUserProfile,
        onSuccess: () => {
            enqueueSnackbar('Datos guardados.', { variant: 'success' });
            queryClient.invalidateQueries({ queryKey: ['userProfile'] });
        },
        onError: (err: unknown) => {
            enqueueSnackbar(axios.isAxiosError(err) && !err.response ? OFFLINE_MESSAGE : 'No hemos podido guardar los datos. Inténtalo de nuevo.', { variant: 'error' });
        },
    });

    const passwordMutation = useMutation({
        mutationFn: updateUserPassword,
        onSuccess: () => {
            enqueueSnackbar('Contraseña cambiada.', { variant: 'success' });
            setCurrentPassword('');
            setPassword('');
            setConfirmPassword('');
            setPasswordErrors({});
        },
        onError: (err: unknown) => {
            const code = errorCode(err);
            if (code === 'CURRENT_PASSWORD_INVALID') {
                setPasswordErrors({ current: 'La contraseña actual no es correcta.' });
                document.getElementById('profile-current')?.focus();
            } else if (code === 'PASSWORD_CHECK_RATE_LIMITED') {
                setPasswordFormError('Has hecho demasiados intentos. Espera unos minutos y vuelve a probar.');
            } else if (axios.isAxiosError(err) && !err.response) {
                setPasswordFormError(OFFLINE_MESSAGE);
            } else {
                setPasswordFormError('No hemos podido cambiar la contraseña. Inténtalo de nuevo.');
            }
        },
    });

    const handleProfileSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!fullName.trim()) {
            setNameError('Escribe tu nombre y apellidos.');
            document.getElementById('profile-name')?.focus();
            return;
        }
        profileMutation.mutate({ full_name: fullName.trim() });
    };

    const passwordFieldError = (key: keyof PasswordErrors): string | undefined => {
        if (key === 'current') return currentPassword ? undefined : 'Escribe tu contraseña actual.';
        if (key === 'next') {
            if (password.length < MIN_PASSWORD_LENGTH) return `La contraseña necesita al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
            return password === currentPassword ? 'La contraseña nueva tiene que ser distinta de la actual.' : undefined;
        }
        return confirmPassword && confirmPassword === password ? undefined : 'Las contraseñas no coinciden.';
    };

    const blurPassword = (key: keyof PasswordErrors, value: string) => () => {
        if (!value) return;
        setPasswordErrors((x) => ({ ...x, [key]: passwordFieldError(key) }));
    };

    const handlePasswordSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setPasswordFormError(null);
        const errors: PasswordErrors = {
            current: passwordFieldError('current'),
            next: passwordFieldError('next'),
            repeat: passwordFieldError('repeat'),
        };
        setPasswordErrors(errors);
        if (errors.current || errors.next || errors.repeat) {
            focusFirstError([[errors.current, 'profile-current'], [errors.next, 'profile-new'], [errors.repeat, 'profile-repeat']]);
            return;
        }
        passwordMutation.mutate({ current_password: currentPassword, new_password: password });
    };

    const page = (children: React.ReactNode) => (
        <Box sx={{ width: '100%', px: { xs: 2, sm: 3, lg: 4 }, pt: 3, pb: 6 }}>
            <PageHeader
                eyebrow="Mi cuenta"
                title="Mi perfil"
                back="origin"
                meta={planLabel ? <HeaderTag>Plan {planLabel}</HeaderTag> : undefined}
            />
            {children}
        </Box>
    );

    if (isLoading || isPricingContextLoading) {
        return page(
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5, maxWidth: 1120 }}>
                {[0, 1].map((i) => (
                    <Box key={i} sx={{ height: 360, borderRadius: '16px', bgcolor: 'rgba(148,163,184,.08)' }} />
                ))}
            </Box>,
        );
    }

    if (error || pricingContextError) {
        return page(
            <Box sx={{ maxWidth: 560 }}>
                <StatusBlock
                    variant="error"
                    title="No hemos podido cargar tu perfil"
                    text="Puede ser un problema de conexión. Vuelve a intentarlo."
                    actions={<JourneyButton onClick={() => { void refetch(); void refetchPricingContext(); }}>Reintentar</JourneyButton>}
                />
            </Box>,
        );
    }

    return page(
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5, alignItems: 'start', maxWidth: 1120 }}>
            <JourneyCard sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <JourneyText variant="h3" component="h2">Datos personales</JourneyText>
                    <JourneyText variant="small">Cómo te ve tu equipo en PROA.</JourneyText>
                </Box>
                <Box component="form" noValidate onSubmit={handleProfileSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <JourneyField
                        id="profile-name" label="Nombre y apellidos" value={fullName} autoComplete="name" autoFocus
                        error={nameError}
                        onChange={(e) => { setFullName(e.target.value); setNameError(undefined); }}
                        onBlur={() => setNameError(fullName.trim() ? undefined : 'Escribe tu nombre y apellidos.')}
                    />
                    <JourneyField
                        id="profile-email" label="Email" type="email" value={profile?.email ?? ''} autoComplete="email" readOnly
                        help="Es el email con el que entras. Para cambiarlo, escríbenos a soporte@proapricing.ai."
                        sx={{ color: 'rgba(248,250,252,.6)' }}
                    />
                    <JourneyField
                        id="profile-company" label="Empresa" value={companyName} autoComplete="organization" readOnly
                        help="La empresa de la cuenta. La cambia el administrador."
                        sx={{ color: 'rgba(248,250,252,.6)' }}
                    />
                    <Box>
                        <JourneyButton type="submit" disabled={profileMutation.isPending}>
                            {profileMutation.isPending ? 'Guardando…' : 'Guardar cambios'}
                        </JourneyButton>
                    </Box>
                </Box>
            </JourneyCard>

            <JourneyCard sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <JourneyText variant="h3" component="h2">Seguridad</JourneyText>
                    <JourneyText variant="small">Cambia tu contraseña. Necesitas la actual.</JourneyText>
                </Box>
                <Box component="form" noValidate onSubmit={handlePasswordSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* El gestor de contraseñas asocia la nueva a este email. */}
                    {profile?.email && <input type="email" name="username" autoComplete="username" value={profile.email} readOnly hidden />}
                    <PasswordField
                        id="profile-current" label="Contraseña actual" value={currentPassword} autoComplete="current-password"
                        error={passwordErrors.current}
                        onChange={(e) => { setCurrentPassword(e.target.value); setPasswordErrors((x) => ({ ...x, current: undefined })); }}
                        onBlur={blurPassword('current', currentPassword)}
                    />
                    <PasswordField
                        id="profile-new" label="Contraseña nueva" value={password} autoComplete="new-password" showRequirements
                        error={passwordErrors.next}
                        onChange={(e) => { setPassword(e.target.value); setPasswordErrors((x) => ({ ...x, next: undefined })); }}
                        onBlur={blurPassword('next', password)}
                    />
                    <PasswordField
                        id="profile-repeat" label="Repite la contraseña nueva" value={confirmPassword} autoComplete="new-password"
                        error={passwordErrors.repeat}
                        onChange={(e) => { setConfirmPassword(e.target.value); setPasswordErrors((x) => ({ ...x, repeat: undefined })); }}
                        onBlur={blurPassword('repeat', confirmPassword)}
                    />
                    {passwordFormError && <Notice tone="danger">{passwordFormError}</Notice>}
                    <Box>
                        <JourneyButton type="submit" disabled={passwordMutation.isPending}>
                            {passwordMutation.isPending ? 'Guardando…' : 'Cambiar contraseña'}
                        </JourneyButton>
                    </Box>
                </Box>
            </JourneyCard>
        </Box>,
    );
};

export default ProfilePage;
